import { Link } from "react-router-dom";
import { EmptyState } from "@/components/new-look/blocks";
import { useIdeaStageCounts } from "@/hooks/queries/use-epic-requests";
import { useMachines } from "@/hooks/queries/use-mailbox";
import { useTeamTree } from "@/hooks/queries/use-teams";
import { usePageTitle } from "@/hooks/use-page-title";
import { machineServicesRemote, type Machine } from "@/lib/api/mailbox";
import type { TeamTreeNode } from "@/lib/api/types";
import { cn } from "@/lib/utils";
import { projectsOf } from "../teams/all-teams";

/**
 * The new look's Projects page (docs/new-look/PLAN.md, step 7.2): every
 * project (today's teams that hold work) with its repository, its team, which
 * of your machines can build it, where it's live and how many ideas it has.
 * Settings stay on the project's own page.
 */

export interface ProjectRow {
  project: TeamTreeNode;
  /** The top-level team it sits under, or null when it is a team on its own. */
  team: TeamTreeNode | null;
}

/** Every project in the tree, with the top-level team it belongs to. */
export function projectRows(tree: readonly TeamTreeNode[]): ProjectRow[] {
  return tree.flatMap((top) =>
    projectsOf(top).map((project) => ({ project, team: project.id === top.id ? null : top }))
  );
}

/** "owner/repo" from a repository address, or null. */
export function repoName(remote: string | null | undefined): string | null {
  if (!remote) return null;
  const match = /[:/]([^/:]+\/[^/]+?)(?:\.git)?\/?$/.exec(remote.trim());
  return match?.[1] ?? remote;
}

/** Where the project is live, from the environments it has deployed to. */
/** Shown when CompBox hasn't deployed the project (it may still be live elsewhere). */
export const NOT_LIVE = "—";

export function liveText(deployedEnvs: string | undefined): string {
  let envs: unknown = [];
  try {
    envs = JSON.parse(deployedEnvs ?? "[]");
  } catch {
    envs = [];
  }
  const list = Array.isArray(envs) ? envs.filter((e): e is string => typeof e === "string") : [];
  return list.length > 0 ? list.join(", ") : NOT_LIVE;
}

/** Your machines that have this project's repository. */
export function buildsOn(project: TeamTreeNode, machines: readonly Machine[]): string[] {
  if (!project.gitRemote) return [];
  return machines.filter((m) => machineServicesRemote(m, project.gitRemote)).map((m) => m.name);
}

const GRID =
  "grid grid-cols-[minmax(0,1fr)_auto] gap-3 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1.6fr)_minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,0.6fr)]";

function IdeaCount({ teamId }: { teamId: string }) {
  const { data } = useIdeaStageCounts({ teamId });
  return <>{data ? String(data.total) : "—"}</>;
}

function Row({ row, machines }: { row: ProjectRow; machines: readonly Machine[] }) {
  const { project, team } = row;
  const repo = repoName(project.gitRemote);
  const on = buildsOn(project, machines);
  const live = liveText(project.stackDeployedEnvs);
  return (
    <li>
      <Link
        to={`/teams/${project.id}`}
        className={cn(
          GRID,
          "items-center border-b border-border px-4 py-3.5 transition-colors hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none md:px-5"
        )}
      >
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-[15px] font-semibold text-foreground">{project.name}</span>
          <span className="truncate text-[13px] text-muted-foreground md:hidden">
            {repo ?? "No repository yet"}
          </span>
        </span>
        <span className="hidden truncate text-sm text-foreground/80 md:block">
          {team?.name ?? "—"}
        </span>
        <span
          className={cn(
            "hidden truncate font-mono text-[12.5px] md:block",
            repo ? "text-foreground/80" : "text-muted-foreground"
          )}
        >
          {repo ?? "No repository yet"}
        </span>
        <span className="hidden truncate text-sm text-foreground/80 md:block">
          {!project.gitRemote ? "—" : on.length > 0 ? on.join(", ") : "None of your machines"}
        </span>
        <span
          className={cn(
            "justify-self-end truncate text-sm md:justify-self-start",
            live === NOT_LIVE ? "text-muted-foreground" : "text-foreground/80"
          )}
        >
          {live}
        </span>
        <span className="hidden text-sm tabular-nums text-foreground/80 md:block">
          <IdeaCount teamId={project.id} />
        </span>
      </Link>
    </li>
  );
}

export function AllProjectsPage() {
  usePageTitle("Projects");
  const { data: tree = [], isLoading, isError } = useTeamTree();
  const { data: machines = [] } = useMachines();
  const rows = projectRows(tree);

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-foreground">Projects</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Each project's repository, which of your machines can build it, and where it's live. Open
          one to connect a repository or change its settings.
        </p>
      </div>

      {isLoading ? (
        <div aria-busy="true" aria-label="Loading projects" className="flex flex-col gap-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-14 animate-pulse rounded-xl bg-muted" />
          ))}
        </div>
      ) : isError ? (
        <EmptyState
          title="Couldn't load projects"
          body="Check your connection and refresh the page."
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No projects yet"
          body="A project is where ideas get built. Add one under a team."
          action={
            <Link
              to="/teams"
              className="inline-flex min-h-11 items-center rounded-lg border border-border px-4 text-sm font-medium hover:bg-muted"
            >
              Go to Teams
            </Link>
          }
        />
      ) : (
        <section aria-label="Projects" className="overflow-hidden rounded-2xl border border-border">
          <div
            aria-hidden="true"
            className={cn(
              GRID,
              "hidden border-b border-border bg-muted/50 px-5 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground md:grid"
            )}
          >
            <span>Project</span>
            <span>Team</span>
            <span>Repository</span>
            <span>Builds on</span>
            <span>Live</span>
            <span>Ideas</span>
          </div>
          <ul>
            {rows.map((row) => (
              <Row key={row.project.id} row={row} machines={machines} />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
